import { useMutation } from '@tanstack/react-query';

import { useTRPC } from '@/shared/api';
import type { SurveyChoiceKey } from '@/shared/config';

/**
 * 서버가 받는 1~7번 답변 — api 의 crm/dto/survey.schema.ts 와 같은 모양이다.
 * 이 이름들이 DB 의 answers 칸에 그대로 키로 남으므로, 화면 사정으로 바꾸지 않는다.
 */
export type SurveyAnswers = {
  role: string;
  timeSpent: string[];
  tools: string[];
  aiExperience: string;
  wantCases: string[];
  reason: string;
  story: string;
} & Partial<Record<`${Exclude<SurveyChoiceKey, 'aiExperience'>}Other`, string>>;

/** 설문 제출 한 건. */
export interface SurveySubmission {
  answers: SurveyAnswers;
  email: string;
  wantsConsulting: boolean;
  /** [필수] 동의 없이는 보낼 수 없다 — 서버도 `literal(true)` 로 막는다. */
  agreedToPrivacy: true;
  /** [선택] 소식 메일 동의. 고르지 않아도 참여할 수 있다. */
  marketingOptIn: boolean;
  /** 허니팟 — 사람에게 보이지 않는 칸이라 값이 있으면 자동 제출이다. 서버가 조용히 버린다. */
  homepage?: string;
}

/** 화면이 실제로 쓰는 것만 내보낸다 (useSubmitInquiry 와 같은 판단). */
export interface SurveySubmitter {
  submit: (submission: SurveySubmission) => void;
  /** 보내는 중. 두 번 눌리면 같은 응답이 두 건으로 쌓인다. */
  isPending: boolean;
  /** 서버가 받았다는 사실이 곧 제출이다. 화면이 따로 기억하지 않는다. */
  isSuccess: boolean;
  /** 실패 사유. 문구는 서버가 정하고 화면은 그대로 보여준다. */
  error: unknown;
}

/** 설문 제출 (/survey). */
export function useSubmitSurvey(): SurveySubmitter {
  const trpc = useTRPC();
  const mutation = useMutation(trpc.crm.submitSurvey.mutationOptions());

  return {
    submit: (submission) => mutation.mutate(submission),
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
  };
}
